import { useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Download, Upload, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  parseWorkbook,
  validateRows,
  buildTemplateBlob,
  isImportable,
  type ParsedRow,
} from '@/lib/services/excel/customerImport';
import { nameTranslationService } from '@/lib/services/nameTranslationService';
import { isPhoneAvailable } from '@/lib/services/otp-verification';
import { useAuth } from '@/contexts/AuthContext';
import { getGroupHierarchy } from '@/lib/firebase/queries/hierarchical-groups';
import { addGroupMember, getGroupName, SubgroupConflictError } from '@/lib/firebase/queries/group-members';
import { membershipConflictMessage } from '@/lib/groups/membershipConflictCopy';
import {
  resolveImportGroup,
  describeAssignment,
  flattenHierarchy,
  type OrgGroup,
} from '@/lib/services/excel/importGroupAssignment';

interface ImportCustomersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

type BatchRow = {
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  phone: string;
};

type BatchResult = {
  results: { index: number; phone: string; status: string; uid?: string; reason?: string }[];
  created: number;
  skipped: number;
  errored: number;
};

// Translate the valid rows' English names with a bounded concurrency so a big
// sheet does not fire hundreds of parallel AI calls.
async function fillEnglish(rows: ParsedRow[]): Promise<ParsedRow[]> {
  const out = [...rows];
  const targets = out.filter((r) => isImportable(r.status));
  const LIMIT = 5;
  let cursor = 0;
  async function worker() {
    while (cursor < targets.length) {
      const r = targets[cursor++];
      const { firstNameEn, lastNameEn } = await nameTranslationService.translateToEnglish(r.firstNameAr, r.lastNameAr);
      r.firstNameEn = firstNameEn;
      r.lastNameEn = lastNameEn;
    }
  }
  await Promise.all(Array.from({ length: Math.min(LIMIT, targets.length) }, worker));
  return out;
}

export function ImportCustomersDialog({ open, onOpenChange, organizationId, onSuccess }: ImportCustomersDialogProps) {
  const { dir } = useLanguage();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'parsing' | 'ready' | 'importing' | 'done'>('idle');
  const { currentUser } = useAuth();
  // #68: the organization's sections and groups, read once per file.
  const [orgGroups, setOrgGroups] = useState<OrgGroup[]>([]);
  // Rows imported but left without a group, shown after the import.
  const [unassigned, setUnassigned] = useState<{ rowNumber: number; name: string; reason: string }[]>([]);
  const [importSummary, setImportSummary] = useState('');
  const hasGroupColumns = rows.some((r) => r.sectionName || r.groupName);
  const assignmentOf = (r: ParsedRow) => resolveImportGroup(orgGroups, r.sectionName, r.groupName);

  const validCount = rows.filter((r) => isImportable(r.status)).length;
  const noPhoneCount = rows.filter((r) => r.status === 'no-phone').length;

  function downloadTemplate() {
    const url = URL.createObjectURL(buildTemplateBlob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'namat-customers-template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      setPhase('parsing');
      const buffer = await file.arrayBuffer();
      let parsed = validateRows(parseWorkbook(buffer));

      // Existing-account check for the still-valid rows.
      await Promise.all(
        parsed.map(async (r) => {
          if (r.status !== 'valid') return;
          const { available } = await isPhoneAvailable(r.phone);
          if (!available) r.status = 'already-registered';
        }),
      );

      parsed = await fillEnglish(parsed);
      if (parsed.some((r) => r.sectionName || r.groupName)) {
        setOrgGroups(flattenHierarchy(await getGroupHierarchy(organizationId)));
      }
      setRows(parsed);
      setPhase('ready');
    } catch (err) {
      console.error('parse failed:', err);
      toast.error('Could not read this file. Use the provided template.');
      setPhase('idle');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function editEnglish(rowNumber: number, key: 'firstNameEn' | 'lastNameEn', value: string) {
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, [key]: value } : r)));
  }

  function removeRow(rowNumber: number) {
    setRows((prev) => prev.filter((r) => r.rowNumber !== rowNumber));
  }

  async function doImport() {
    const importRows = rows.filter((r) => isImportable(r.status));
    const payload: BatchRow[] = importRows
      .map((r) => ({
        firstNameEn: r.firstNameEn || r.firstNameAr,
        lastNameEn: r.lastNameEn || r.lastNameAr,
        firstNameAr: r.firstNameAr,
        lastNameAr: r.lastNameAr,
        phone: r.phone, // '' for a no-phone row (#71)
      }));
    if (payload.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    try {
      setBusy(true);
      setPhase('importing');
      const batch = httpsCallable<{ organizationId: string; rows: BatchRow[] }, BatchResult>(functions, 'createManagedUsersBatch');
      const { data } = await batch({ organizationId, rows: payload });

      // #68: place each created beneficiary in its existing group; unknown
      // section/group names are reported, never created.
      const isRtl = dir === 'rtl';
      const left: { rowNumber: number; name: string; reason: string }[] = [];
      let assigned = 0;
      for (const res of data.results) {
        const row = importRows[res.index];
        if (!row || res.status !== 'created' || !res.uid) continue;
        const a = assignmentOf(row);
        if (a.kind === 'none') continue;
        const name = `${row.firstNameAr} ${row.lastNameAr}`;
        if (a.kind !== 'group') {
          left.push({ rowNumber: row.rowNumber, name, reason: describeAssignment(a, isRtl) });
          continue;
        }
        let ok = false;
        let conflict: string | null = null;
        try {
          ok = !!currentUser && await addGroupMember(a.groupId, res.uid, currentUser.uid);
        } catch (e) {
          console.error('group assignment failed:', e);
          // namat#30: one group per organization.
          if (e instanceof SubgroupConflictError) {
            conflict = membershipConflictMessage('manager', isRtl, await getGroupName(e.existingGroupId));
          }
        }
        if (ok) assigned++;
        else if (conflict) left.push({ rowNumber: row.rowNumber, name, reason: conflict });
        else left.push({ rowNumber: row.rowNumber, name, reason: isRtl ? `تعذّرت الإضافة إلى «${a.groupName}»` : `Could not add to "${a.groupName}"` });
      }

      const summary = isRtl
        ? `أُضيف ${data.created}، تُخطّي ${data.skipped}، فشل ${data.errored}` + (hasGroupColumns ? `، وُزّع على المجموعات ${assigned}` : '')
        : `Imported ${data.created}, skipped ${data.skipped}, failed ${data.errored}` + (hasGroupColumns ? `, placed in groups ${assigned}` : '');
      toast.success(summary);
      onSuccess?.();
      setRows([]);
      if (left.length > 0) {
        setImportSummary(summary);
        setUnassigned(left);
        setPhase('done');
      } else {
        onOpenChange(false);
        setPhase('idle');
      }
    } catch (error) {
      console.error('import failed:', error);
      const err = error as { message?: string; code?: string } | undefined;
      const msg = String(err?.message || '');
      if (msg.includes('too many rows')) toast.error('Too many rows (max 200 per import)');
      else if (err?.code === 'functions/permission-denied') toast.error('You are not authorized to import customers');
      else toast.error('Import failed');
      setPhase('ready');
    } finally {
      setBusy(false);
    }
  }

  const statusColor: Record<string, string> = {
    valid: 'text-green-600',
    'no-phone': 'text-amber-600',
    'invalid-phone': 'text-red-600',
    'missing-name': 'text-red-600',
    'duplicate-in-file': 'text-amber-600',
    'already-registered': 'text-amber-600',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing the post-import summary must not leave it for the next open.
        if (!next && phase === 'done') { setUnassigned([]); setPhase('idle'); }
        onOpenChange(next);
      }}
    >
      <DialogContent dir={dir} className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{dir === 'rtl' ? 'استيراد المستفيدين من Excel' : 'Import customers from Excel'}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={downloadTemplate} disabled={busy}>
            <Download className="h-4 w-4 mr-2" />
            {dir === 'rtl' ? 'تنزيل القالب' : 'Download template'}
          </Button>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Upload className="h-4 w-4 mr-2" />
            {dir === 'rtl' ? 'رفع ملف' : 'Upload file'}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} />
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>

        {phase === 'done' && (
          <div className="space-y-2" data-testid="import-summary">
            <div className="text-sm font-medium">{importSummary}</div>
            <div className="text-sm text-amber-700">
              {dir === 'rtl'
                ? `${unassigned.length} أُضيفوا للمنظمة دون مجموعة:`
                : `${unassigned.length} were added to the organization without a group:`}
            </div>
            <div className="max-h-60 overflow-auto border rounded">
              <table className="w-full text-sm">
                <tbody>
                  {unassigned.map((u) => (
                    <tr key={u.rowNumber} className="border-t">
                      <td className="p-2">{u.rowNumber}</td>
                      <td className="p-2" dir="rtl">{u.name}</td>
                      <td className="p-2 text-amber-700">{u.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button type="button" onClick={() => { setUnassigned([]); setPhase('idle'); onOpenChange(false); }}>
                {dir === 'rtl' ? 'تم' : 'Done'}
              </Button>
            </div>
          </div>
        )}

        {phase === 'ready' && (
          <>
            <div className="text-sm text-muted-foreground">
              {dir === 'rtl' ? `صالح: ${validCount} / ${rows.length}` : `Valid: ${validCount} / ${rows.length}`}
            </div>
            {noPhoneCount > 0 && (
              <div className="text-sm text-amber-700" data-testid="no-phone-note">
                {dir === 'rtl'
                  ? `${noPhoneCount} بدون رقم جوال: سيُضافون إلى المنظمة ويظهرون في القوائم والفحوصات، ولا يستطيعون تسجيل الدخول حتى يُضاف لهم رقم جوال.`
                  : `${noPhoneCount} without a phone: they are added to the organization and appear in lists and exams, but cannot sign in until a phone number is added.`}
              </div>
            )}
            <div className="max-h-80 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-start">#</th>
                    <th className="p-2 text-start">{dir === 'rtl' ? 'الاسم (عربي)' : 'Name (AR)'}</th>
                    <th className="p-2 text-start">{dir === 'rtl' ? 'الاسم (إنجليزي)' : 'Name (EN)'}</th>
                    <th className="p-2 text-start">{dir === 'rtl' ? 'الجوال' : 'Phone'}</th>
                    {hasGroupColumns && (
                      <th className="p-2 text-start">{dir === 'rtl' ? 'القسم / المجموعة' : 'Section / group'}</th>
                    )}
                    <th className="p-2 text-start">{dir === 'rtl' ? 'الحالة' : 'Status'}</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.rowNumber} className="border-t">
                      <td className="p-2">{r.rowNumber}</td>
                      <td className="p-2" dir="rtl">{r.firstNameAr} {r.lastNameAr}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Input value={r.firstNameEn} onChange={(e) => editEnglish(r.rowNumber, 'firstNameEn', e.target.value)} className="h-8" disabled={!isImportable(r.status)} />
                          <Input value={r.lastNameEn} onChange={(e) => editEnglish(r.rowNumber, 'lastNameEn', e.target.value)} className="h-8" disabled={!isImportable(r.status)} />
                        </div>
                      </td>
                      <td className="p-2">{r.phone || '—'}</td>
                      {hasGroupColumns && (() => {
                        const a = assignmentOf(r);
                        return (
                          <td className={`p-2 ${a.kind === 'group' || a.kind === 'none' ? '' : 'text-amber-600'}`} data-testid="row-assignment">
                            {describeAssignment(a, dir === 'rtl')}
                          </td>
                        );
                      })()}
                      <td className={`p-2 ${statusColor[r.status] || ''}`}>{r.status}</td>
                      <td className="p-2">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(r.rowNumber)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={`flex justify-end gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                {dir === 'rtl' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button type="button" onClick={doImport} disabled={busy || validCount === 0}>
                {phase === 'importing' && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {dir === 'rtl' ? `استيراد (${validCount})` : `Import (${validCount})`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
